/**
 * useAnalyticsCurrencies Hook
 * 
 * Fetches currencies for Analytics page.
 * Handles loading state and error handling.
 */

import { useEffect, useState, useRef } from "react";
import { auth } from "../../../firebase";
import {
  collection,
  query,
  onSnapshot,
  getDocs,
  doc,
  getDoc,
  Timestamp,
  QuerySnapshot,
  DocumentSnapshot,
  Unsubscribe,
  FirestoreError,
} from "firebase/firestore";
import { db } from "../../../firebase";
import { Currency } from "../../../types";
import { isDemoMode } from "../../../utils/demoMode";
import { isBypassAuthEnabled } from "../../../utils/bypassAuth";

export interface UseAnalyticsCurrenciesParams {
  workspaceId: string;
  defaultCurrency: string;
}

export interface UseAnalyticsCurrenciesResult {
  currencies: Currency[];
  setCurrencies: React.Dispatch<React.SetStateAction<Currency[]>>;
}

/**
 * Hook for fetching currencies for Analytics
 */
export const useAnalyticsCurrencies = ({
  workspaceId,
  defaultCurrency,
}: UseAnalyticsCurrenciesParams): UseAnalyticsCurrenciesResult => {
  const [currencies, setCurrencies] = useState<Currency[]>([]);
  const previousDefaultCurrencyRef = useRef<string>(defaultCurrency);
  // Store current defaultCurrency in a ref that we can check in the snapshot callback
  const currentDefaultCurrencyRef = useRef<string>(defaultCurrency);
  // Track when defaultCurrency last changed to ignore stale snapshots
  const currencyChangeTimestampRef = useRef<number>(0);

  // Update ref synchronously when defaultCurrency changes (before useEffect runs)
  // This ensures snapshot callbacks see the latest value immediately
  if (currentDefaultCurrencyRef.current !== defaultCurrency) {
    console.log('[useAnalyticsCurrencies] Default currency changed synchronously', {
      from: currentDefaultCurrencyRef.current,
      to: defaultCurrency,
      timestamp: new Date().toISOString()
    });
    currencyChangeTimestampRef.current = Date.now();
    currentDefaultCurrencyRef.current = defaultCurrency;
  }

  useEffect(() => {
    // Allow loading in demo mode or when authenticated
    if (!workspaceId || (!auth.currentUser && !isDemoMode() && !isBypassAuthEnabled())) return;

    // Update current ref with latest defaultCurrency value (redundant but safe)
    currentDefaultCurrencyRef.current = defaultCurrency;

    // If defaultCurrency changed, note it but don't clear currencies yet
    // The snapshot callback will update rates using the new defaultCurrency from the rates object
    // Clearing would cause a brief empty state that prevents calculations
    if (previousDefaultCurrencyRef.current !== defaultCurrency) {
      console.log('[useAnalyticsCurrencies] Default currency changed, will update rates on next snapshot', {
        from: previousDefaultCurrencyRef.current,
        to: defaultCurrency,
        timestamp: new Date().toISOString()
      });
      previousDefaultCurrencyRef.current = defaultCurrency;
    }

    console.log('[useAnalyticsCurrencies] Starting currency fetch', {
      workspaceId,
      defaultCurrency,
      timestamp: new Date().toISOString()
    });

    const currenciesRef = collection(db, "workspaces", workspaceId, "currencies");
    const basicQuery = query(currenciesRef);
    
    const unsubscribeCurrencies = onSnapshot(
      basicQuery,
      async (snapshot) => {
        // Check current defaultCurrency value (not from closure) to see if it changed
        // Firestore stores rates for all default currencies in the rates object,
        // so we can process the snapshot as long as we use the current defaultCurrency
        const currentDefaultCurrency = currentDefaultCurrencyRef.current;
        
        // Update previous ref if it changed (for logging)
        if (previousDefaultCurrencyRef.current !== currentDefaultCurrency) {
          console.log('[useAnalyticsCurrencies] Processing snapshot with new defaultCurrency', {
            oldDefaultCurrency: previousDefaultCurrencyRef.current,
            newDefaultCurrency: currentDefaultCurrency,
            timestamp: new Date().toISOString()
          });
          previousDefaultCurrencyRef.current = currentDefaultCurrency;
        }
        
        console.log('[useAnalyticsCurrencies] Currency snapshot received', {
          docsCount: snapshot.docs.length,
          defaultCurrency: currentDefaultCurrency,
          timestamp: new Date().toISOString()
        });
        const currenciesList: Currency[] = snapshot.docs
          .map((docSnap: DocumentSnapshot) => {
            const data = docSnap.data();
            if (!data) return null;

            const rates = (data.rates as Record<string, number | null>) || {};
            let currentRate: number | null;
            // Use currentDefaultCurrency from above (already declared)
            if (rates[currentDefaultCurrency] !== undefined) {
              currentRate = rates[currentDefaultCurrency];
            } else if (typeof data.rate === "number") {
              currentRate = data.rate;
            } else if (data.rate === null || data.rate === undefined) {
              currentRate = null;
            } else {
              const parsed = parseFloat(String(data.rate));
              currentRate = isNaN(parsed) ? null : parsed;
            }

            return {
              code: docSnap.id,
              name: (data.name as string) || "",
              symbol: (data.symbol as string) || "",
              rate: currentRate,
              rates: rates,
              manuallyEdited: data.manuallyEdited === true,
              decimalPlaces: data.decimalPlaces !== undefined ? (data.decimalPlaces as number) : undefined,
              updatedAt: data.updatedAt || data.createdAt || Timestamp.now(),
            } as Currency;
          })
          .filter((c): c is Currency => c !== null);

        // Check if default currency is in the list, if not, fetch it separately
        // currentDefaultCurrency already declared above
        const defaultCurrencyInList = currenciesList.find((c) => c.code === currentDefaultCurrency);
        if (!defaultCurrencyInList && currentDefaultCurrency) {
          try {
            const defaultCurrencyDocRef = doc(db, "workspaces", workspaceId, "currencies", currentDefaultCurrency);
            const defaultCurrencyDoc = await getDoc(defaultCurrencyDocRef);
            if (defaultCurrencyDoc.exists()) {
              const data = defaultCurrencyDoc.data();
              const rates = (data.rates as Record<string, number | null>) || {};
              const currentRate = rates[currentDefaultCurrency] ?? (typeof data.rate === "number" ? data.rate : 1.0);
              currenciesList.push({
                code: currentDefaultCurrency,
                name: (data.name as string) || "",
                symbol: (data.symbol as string) || "",
                rate: currentRate,
                rates: rates,
                manuallyEdited: data.manuallyEdited === true,
                decimalPlaces: data.decimalPlaces !== undefined ? (data.decimalPlaces as number) : undefined,
                updatedAt: data.updatedAt || data.createdAt || Timestamp.now(),
              } as Currency);
            }
          } catch (error) {
            console.error("Error fetching default currency:", error);
          }
        }

        console.log('[useAnalyticsCurrencies] Currencies updated', {
          currenciesCount: currenciesList.length,
          defaultCurrency: currentDefaultCurrency,
          hasDefaultCurrency: currenciesList.some(c => c.code === currentDefaultCurrency),
          currencyCodes: currenciesList.map(c => c.code),
          timestamp: new Date().toISOString()
        });
        setCurrencies(currenciesList);
      },
      async (error: FirestoreError) => {
        console.error("Error fetching currencies:", error);
        // Fallback query without onSnapshot
        try {
          const snapshot = await getDocs(currenciesRef);
          const currenciesList: Currency[] = snapshot.docs
            .map((docSnap: DocumentSnapshot) => {
              const data = docSnap.data();
              if (!data) return null;
              const rates = (data.rates as Record<string, number | null>) || {};
              const currentRate = rates[defaultCurrency] ?? (typeof data.rate === "number" ? data.rate : null);
              return {
                code: docSnap.id,
                name: (data.name as string) || "",
                symbol: (data.symbol as string) || "",
                rate: currentRate,
                rates: rates,
                manuallyEdited: data.manuallyEdited === true,
                decimalPlaces: data.decimalPlaces !== undefined ? (data.decimalPlaces as number) : undefined,
                updatedAt: data.updatedAt || data.createdAt || Timestamp.now(),
              } as Currency;
            })
            .filter((c: Currency | null): c is Currency => c !== null);

          // Check if default currency is in the list, if not, fetch it separately
          const defaultCurrencyInList = currenciesList.find((c) => c.code === defaultCurrency);
          if (!defaultCurrencyInList && defaultCurrency) {
            try {
              const defaultCurrencyDocRef = doc(db, "workspaces", workspaceId, "currencies", defaultCurrency);
              const defaultCurrencyDoc = await getDoc(defaultCurrencyDocRef);
              if (defaultCurrencyDoc.exists()) {
                const data = defaultCurrencyDoc.data();
                const rates = (data.rates as Record<string, number | null>) || {};
                const currentRate = rates[defaultCurrency] ?? (typeof data.rate === "number" ? data.rate : 1.0);
                currenciesList.push({
                  code: defaultCurrency,
                  name: (data.name as string) || "",
                  symbol: (data.symbol as string) || "",
                  rate: currentRate,
                  rates: rates,
                  manuallyEdited: data.manuallyEdited === true,
                  decimalPlaces: data.decimalPlaces !== undefined ? (data.decimalPlaces as number) : undefined,
                  updatedAt: data.updatedAt || data.createdAt || Timestamp.now(),
                } as Currency);
              }
            } catch (defaultCurrencyError) {
              console.error("Error fetching default currency in fallback:", defaultCurrencyError);
            }
          }

          setCurrencies(currenciesList);
        } catch (fallbackError: any) {
          console.error("Fallback getDocs also failed:", fallbackError);
          setCurrencies([]);
        }
      }
    );

    return () => {
      unsubscribeCurrencies();
    };
  }, [workspaceId, defaultCurrency]); // Refetch when default currency changes

  return {
    currencies,
    setCurrencies,
  };
};
